<template>
  <!-- サイドメニューが開いている状態 -->
  <div v-if="expand" v-cloak>
    <div class="link" @click="onClick">
      <span class="link-text">
        {{ text }}
      </span>
    </div>

    <!-- 開閉するコンテンツ -->
    <expanded-item :isOpen="isOpen">
      <div class="container">
        <div
          class="link-shrink"
          v-for="child in children"
          :key="child.link"
          :class="{ active: isActive(child) }"
          @click="onLinkClick(child)"
        >
          <img class="sidemenu-icon-shrink" :src="child.icon" />
          <span class="link-text link-text-shrink">{{ child.text }}</span>
        </div>
      </div>
    </expanded-item>
  </div>

  <!-- サイドメニューが閉じている状態 -->
  <div v-else class="closed" @mouseover="onMouseOver" @mouseleave="onMouseLeave">
    <div class="link" :class="{ shrink: true, active: isCurrent }" @click="() => onLinkClick()">
      <img class="sidemenu-icon" :class="{ shrink: true }" :src="icon" :alt="text" />
    </div>
    <div v-if="isMouseHover" class="float-menu">
      <div class="text">{{ text }}</div>
      <div class="container">
        <div v-for="child in children" :key="child.link" class="item" @click="onLinkClick(child)">
          <img class="sidemenu-icon-shrink" :src="child.icon" />
          <span class="link-text link-text-shrink">{{ child.text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ExpandedItem from "@/components/atoms/ExpandedItem.vue";

interface Data {
  isOpen: boolean;
  isCurrent: boolean;
  currentName: string;
  isMouseHover: boolean;
}

interface Methods {
  onClick: () => void;
  onLinkClick: (item: any) => void;
  isActive: (item: any) => boolean;
  onMouseOver: () => void;
  onMouseLeave: () => void;
}

interface Computed {
  isSubPlot: boolean;
}

interface Props {
  link: string;
  text: string;
  icon: string;
  novelId: string;
  expand: boolean;
  children: any[];
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ExpandedItem,
  },
  data() {
    return {
      isOpen: true,
      isCurrent: this.$route.name === this.link,
      currentName: "",
      isMouseHover: false,
    };
  },
  created() {},
  props: {
    link: String,
    text: String,
    icon: String,
    novelId: String,
    expand: {
      type: Boolean,
      default: true,
    },
    children: Array,
  },
  computed: {
    isSubPlot() {
      return !!this.$route.params.plotId;
    },
  },
  methods: {
    onClick() {
      this.isOpen = !this.isOpen;
    },
    onLinkClick(item) {
      const { novelId, link } = this;
      const linkName = item ? item.link : link;

      // サブプロットの場合は、常に遷移させる
      if (this.isSubPlot) {
        this.$router.push({ name: linkName, params: { novelId } });
        return;
      }

      // 現在のルートと遷移先が同じなら処理をスキップ
      if (this.$route.name === linkName && this.$route.params.novelId === novelId) {
        return;
      }

      this.$router.push({ name: linkName, params: { novelId } });
    },
    isActive(item: any) {
      const { link } = item;
      const { name } = this.$route;
      return name === link;
    },
    onMouseOver() {
      this.isMouseHover = true;
    },
    onMouseLeave() {
      this.isMouseHover = false;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.link {
  position: relative;
  @include hover-overlay;
  display: grid;
  align-items: center;
  grid-template-columns: 26px 12px auto 8px 1fr auto;
  margin: auto;
  padding: 12px 16px 12px 16px;
  text-align: left;
  text-decoration: none;
  font-size: var(--font-size-md);
  font-family: "Courier New", Courier, monospace;
  color: var(--color-text-normal);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.shrink {
    grid-template-columns: 1fr;
    place-items: center;
    padding: 16px 8px 16px 8px;
  }

  &-shrink {
    position: relative;
    @include hover-overlay;
    display: grid;
    align-items: center;
    grid-template-columns: 20px 1fr 20px;
    padding-left: 24px;
    padding-right: 16px;
    padding-top: 8px;
    padding-bottom: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &.active {
      background-color: var(--color-surface-subtle);
      text-decoration: none;
    }

    &:hover {
      text-decoration: none;
    }
  }

  &.active {
    background-color: var(--color-surface-subtle);
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
  }

  &:visited {
    color: var(--color-text-normal);
    font-weight: var(--font-weight-bold);
  }
}

.link-text {
  grid-column: 1/3;
  user-select: none;
  word-break: break-word;
  line-height: initial;
  color: var(--color-text-normal);

  &-shrink {
    grid-column: 2/2;
    padding-left: 8px;
    padding-right: 8px;
    max-height: 30px;
  }
}

.sidemenu-icon {
  grid-column: 1/2;
  width: 100%;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }

  &.shrink {
    grid-column: 1/3;
    width: 30px;
  }

  &-shrink {
    height: 20px;
    filter: brightness(0);

    [data-app-theme="dark"] & {
      filter: brightness(0) invert(1);
    }
  }
}

.closed {
  position: relative;

  .float-menu {
    position: fixed;
    margin-left: 60px;
    margin-top: -62px;
    background-color: var(--color-surface-subtle);
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    cursor: default;
    box-shadow: var(--shadow-md);

    .text {
      box-sizing: border-box;
      padding: 0.5em;
      font-size: 1.1em;
      font-weight: var(--font-weight-bold);
      text-align: left;
      color: var(--color-text-normal);
    }

    .container {
      .item {
        position: relative;
        @include hover-overlay;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        padding: 0.5em;
        color: var(--color-text-normal);
        cursor: pointer;
        transition: background-color 0.2s ease;

      }
    }
  }
}
</style>
